/**
 * 拖图替换的单步历史与协同差异
 *
 * 目标图片层使用字段级 LWW 差异同步；拖入源层继续按历史消息顺序执行幂等删除或恢复。
 */
import { Framework, type IHistoryItem } from '@/framework';
import { applyDifferentProps, getDifferentProps } from '@/utils/layer-kit';
import type { ILayerProps } from '@/version-types';

export enum HistoryType {
    /** 拖入蒙版图层替换 */
    DROP_IMAGE = 'dropImage',
}

interface IHistoryDropImageData {
    item: {
        fromLayer: ILayerProps;
        oldTargetLayer: ILayerProps;
        newTargetLayer: ILayerProps;
        layerIndex: number;
        diffInfo?: Record<string, object>;
    };
}

type DropImageStructureMessage =
    | {
          action: 'delete-source';
          sourceLayerId: string;
          sourceSnapshot: ILayerProps;
          targetLayerId: string;
      }
    | {
          action: 'restore-source';
          sourceLayerId: string;
          sourceSnapshot: ILayerProps;
          targetLayerId: string;
      };

const diffLayers = (newLayer: ILayerProps, oldLayer: ILayerProps) =>
    getDifferentProps(newLayer, oldLayer, undefined, new Set(['parentId']));

/** 管理拖图替换的本地撤销重做和协同字段合并 */
export class DropImageHistory extends Framework {
    /**
     * 创建并注册拖图替换历史
     *
     * @param identity - 编辑器实例标识
     * 副作用：向历史服务注册 DROP_IMAGE 类型。
     */
    constructor(identity: string) {
        super(identity);

        this.framework.service.history.register(HistoryType.DROP_IMAGE, {
            owner: 'layer/image/drop-img',
            collaboration: 'supported',
            isLikeDefault: true,
            onRedoUndo: (historyItem: IHistoryItem<IHistoryDropImageData>, isRedo: boolean, isUndo: boolean) => {
                if (historyItem.isDirty) return;
                const { fromLayer, oldTargetLayer, newTargetLayer, diffInfo } = historyItem.data.data.item;
                const targetLayer = this.framework.service.flatLayer.getLayer(oldTargetLayer.id, true);
                if (targetLayer && diffInfo?.[oldTargetLayer.id]) {
                    applyDifferentProps(targetLayer, diffInfo[oldTargetLayer.id]);
                    this.framework.service.frame.addOrUpdateLayer(targetLayer);
                } else if (!historyItem.isRealEditMode) {
                    this.framework.service.frame.addOrUpdateLayer(isRedo ? newTargetLayer : oldTargetLayer);
                }

                if (isUndo) {
                    if (!this.framework.service.flatLayer.getLayer(fromLayer.id)) {
                        this.framework.service.frame.addOrUpdateLayer(fromLayer);
                    }
                    this.framework.service.frame.setActiveLayer(fromLayer.id);
                }
                if (isRedo) {
                    if (this.framework.service.flatLayer.getLayer(fromLayer.id)) {
                        this.framework.service.frame.delLayer(fromLayer.id);
                    }
                    this.framework.service.frame.setActiveLayer(newTargetLayer.id);
                }
            },
            onAfterRedoUndo: (historyItem: IHistoryItem<IHistoryDropImageData>, isRedo: boolean) => {
                const { fromLayer, oldTargetLayer, diffInfo } = historyItem.data.data.item;
                const structure: DropImageStructureMessage = {
                    action: isRedo ? 'delete-source' : 'restore-source',
                    sourceLayerId: fromLayer.id,
                    sourceSnapshot: fromLayer,
                    targetLayerId: oldTargetLayer.id,
                };
                return {
                    message: [diffInfo || {}, structure],
                };
            },
            onAfterWrite: (historyItem: IHistoryItem<IHistoryDropImageData>) => {
                const { fromLayer, oldTargetLayer, newTargetLayer } = historyItem.data.data.item;
                const structure: DropImageStructureMessage = {
                    action: 'delete-source',
                    sourceLayerId: fromLayer.id,
                    sourceSnapshot: fromLayer,
                    targetLayerId: oldTargetLayer.id,
                };
                return {
                    message: [diffLayers(newTargetLayer, oldTargetLayer), structure],
                };
            },
            onBeforeRedoUndo: (historyItem: IHistoryItem<IHistoryDropImageData>, isRedo, isUndo, store) => {
                const { fromLayer, oldTargetLayer, newTargetLayer } = historyItem.data.data.item;
                const targetLayer = this.framework.service.flatLayer.getLayer(oldTargetLayer.id);
                if (isUndo) {
                    historyItem.isDirty = !targetLayer || !!this.framework.service.flatLayer.getLayer(fromLayer.id);
                    historyItem.data.data.item.diffInfo = diffLayers(oldTargetLayer, newTargetLayer);
                    store.set(historyItem.cursor, {
                        isDirty: historyItem.isDirty,
                    });
                }
                if (isRedo) {
                    const beforeData = store.get(historyItem.cursor);
                    historyItem.isDirty = !beforeData || beforeData.isDirty || !targetLayer;
                    if (!historyItem.isDirty) {
                        historyItem.isDirty = !this.framework.service.flatLayer.getLayer(fromLayer.id);
                    }
                    historyItem.data.data.item.diffInfo = diffLayers(newTargetLayer, oldTargetLayer);
                }
            },
            onMessage: (message: [Record<string, object>, DropImageStructureMessage]) => {
                const [diffInfo, structure] = message;
                const targetLayer = this.framework.service.flatLayer.getLayer(structure.targetLayerId, true);
                const targetDiff = diffInfo?.[structure.targetLayerId];
                if (targetLayer && targetDiff) {
                    applyDifferentProps(targetLayer, targetDiff);
                    this.framework.service.frame.addOrUpdateLayer(targetLayer);
                    this.framework.service.frame.tryRefreshActiveLayer(targetLayer.id);
                }

                if (structure.action === 'delete-source') {
                    this.framework.service.frame.tryRemoveActiveLayer(structure.sourceLayerId);
                    if (this.framework.service.flatLayer.getLayer(structure.sourceLayerId)) {
                        this.framework.service.frame.delLayer(structure.sourceLayerId);
                    }
                    return;
                }

                if (!this.framework.service.flatLayer.getLayer(structure.sourceLayerId)) {
                    this.framework.service.frame.addOrUpdateLayer(structure.sourceSnapshot);
                    this.framework.service.frame.tryRefreshActiveLayer(structure.sourceLayerId);
                }
            },
        });
    }
}
